import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useBusiness } from '@/hooks/useConsoleData';
import { Section } from '@/components/console/Section';
import {
  DEFINITIONS, INTERVAL_LABEL, fetchBusiness, foundingEnds, foundingSplit, monthLabel, percent, rateLabel, usd, ymd,
  type Business, type MetricKey,
} from '@/lib/business-data';
import { NOT_MEASURED, num, when } from '@/lib/format';
import s from './console.module.css';

/**
 * Business: is PostureVision making money, and is it keeping people. MRR,
 * ARR, paying members, this month's new and churned, churn rate, revenue
 * churn, ARPU, LTV, trial to paid, founding members, refunds, and the
 * sign-up week cohorts (first check, day two, day 7, day 30). Every figure
 * carries its definition on hover (and on a tap of its ⓘ on a phone); a
 * figure nobody measured is the words "not measured", never 0; team and QA
 * accounts are left out; Stripe figures say when they were fetched.
 */

function Metric({ k, label, value, sub, warn }: { k: MetricKey; label: string; value: ReactNode; sub?: ReactNode; warn?: boolean }) {
  const [show, setShow] = useState(false);
  return (
    <div className={`${s.stat} ${warn ? s.statWarn : ''}`} title={DEFINITIONS[k]} data-metric={k}>
      <span className={s.statLabel}>
        {label}
        <button type="button" className={s.defBtn} aria-label={`What ${label} means`} aria-expanded={show} onClick={() => setShow((v) => !v)}>ⓘ</button>
      </span>
      <span className={s.statVal} data-value>{value}</span>
      {sub !== undefined && sub !== null && <span className={s.statSub}>{sub}</span>}
      {show && <span className={s.defText} data-definition>{DEFINITIONS[k]}</span>}
    </div>
  );
}

/** A table header with its definition on hover. */
const Th = ({ k, children, right }: { k?: MetricKey; children: ReactNode; right?: boolean }) => (
  <th title={k ? DEFINITIONS[k] : undefined} style={right ? { textAlign: 'right' } : undefined}>{children}</th>
);

const personLink = (userId: string | null, name: string | null, email: string | null) =>
  userId ? <Link to={`/people/${encodeURIComponent(userId)}`}>{name || email || userId.slice(0, 8)}</Link> : <span className={s.muted}>not tied to an account</span>;

export function BusinessView({ b, onRefresh, refreshing }: { b: Business; onRefresh?: () => void; refreshing?: boolean }) {
  const r = b.revenue;
  const asOf = b.stripe.fetchedAt ? `Stripe as of ${when(b.stripe.fetchedAt)}` : 'Stripe not fetched';
  const m = r?.month ?? null;
  const fs = b.founding.subscribers;
  const split = foundingSplit(fs);
  return (
    <div className={s.stack}>
      <div className={s.pageHead}>
        <h1 className={s.pageTitle}>Business</h1>
        <span className={s.pageSub} data-panel="as-of">
          {asOf} · held {Math.round(b.stripe.cacheSeconds / 60)} min · team and QA left out · page built {when(b.generatedAt)}
          {onRefresh && <> · <button type="button" className={s.linkBtn} onClick={onRefresh} disabled={refreshing}>{refreshing ? 'Asking Stripe…' : 'Ask Stripe again'}</button></>}
        </span>
      </div>

      {b.stripe.error && <div className={s.notice} data-panel="stripe-error">{b.stripe.error} Every Stripe figure below is not measured until it answers.</div>}
      {!b.store.measured && (
        <div className={s.notice} data-panel="store" title={DEFINITIONS.store}>
          App Store and Google Play: {NOT_MEASURED}. {b.store.reason} The figures below are web (Stripe) only.
        </div>
      )}

      <div className={s.strip} data-panel="revenue">
        <Metric k="mrr" label={b.store.measured ? 'MRR' : 'MRR (web)'} value={usd(r?.mrrCents)} sub={b.store.measured ? `web ${usd(r?.mrrCents)} · store ${usd(b.store.mrrCents)}` : 'store not measured'} />
        <Metric k="arr" label="ARR" value={usd(r?.arrCents)} />
        <Metric k="paying" label="Paying members" value={num(b.paying.total ?? b.paying.web)} sub={b.store.measured ? `web ${num(b.paying.web)} · store ${num(b.paying.store)}` : `web only · ${num(r?.trialing)} trialing · ${num(r?.pastDue)} past due`} />
        <Metric k="arpu" label="ARPU" value={usd(r?.ltv.arpuCents)} sub="per paying member, per month" />
        <Metric k="ltv" label="LTV" value={usd(r?.ltv.ltvCents)} sub={r?.ltv.note ?? (r ? `churn over ${r.churn.monthsMeasured} complete months` : undefined)} warn={!!r?.ltv.caveat} />
      </div>

      <div className={s.strip} data-panel="month">
        <Metric k="newMonth" label="New this month" value={num(m?.newCount)} sub={m ? monthLabel(m.month) : undefined} />
        <Metric k="churnedMonth" label="Churned this month" value={num(m?.churnedCount)} sub={m ? `${m.churnedFromStart} of the ${m.startCount} paying on the 1st` : undefined} />
        <Metric k="churnRate" label="Monthly churn" value={percent(m?.churnRate)} sub={r ? `pooled over complete months: ${percent(r.churn.monthlyChurn)}` : undefined} />
        <Metric k="grossRevenueChurn" label="Gross revenue churn" value={percent(m?.grossRevenueChurn)} sub={m ? `${usd(m.churnedMrrCents)} of ${usd(m.startMrrCents)} MRR` : undefined} />
        <Metric k="trialToPaid" label="Trial to paid" value={percent(r?.trials.conversion, 0)} sub={r ? `${r.trials.converted} of ${r.trials.started - r.trials.open} ended trials · ${r.trials.open} running` : undefined} />
        <Metric k="founding" label="Founding members" value={num(split ? split.coupon : null)} sub={`on the founding price · ${split ? `${split.intro} with the earlier intro · ` : ''}codes closed ${ymd(b.founding.codesEndOn)} · program ${b.founding.program.length}`} />
        <Metric k="refunds" label="Refunds" value={num(r?.money.refunds.count)} sub={r ? `${usd(r.money.refunds.cents)} all time · ${r.money.refunds.last90dCount} in 90 days` : undefined} />
        <Metric k="collected" label="Collected, 90 days" value={usd(r?.money.collected90dCents)} sub={r ? `30 days ${usd(r.money.collected30dCents)} · all time ${usd(r.money.collectedLifetimeCents)}, net ${usd(r.money.netLifetimeCents)}` : undefined} />
      </div>

      <Section title="Month by month" summary="paying on the 1st, who joined, who left, and the MRR they carried" defaultOpen id="months">
        {!r ? <div className={s.empty}>{NOT_MEASURED}: Stripe did not answer.</div> : (
          <div className={s.tableWrap}>
            <table className={s.table} data-table="months">
              <thead><tr><th>Month</th><Th right>Paying on the 1st</Th><Th k="newMonth" right>New</Th><Th k="churnedMonth" right>Ended</Th><Th k="churnRate" right>Churn</Th><Th right>MRR on the 1st</Th><Th k="grossRevenueChurn" right>MRR lost</Th><Th k="grossRevenueChurn" right>Revenue churn</Th></tr></thead>
              <tbody>
                {[...r.months].reverse().map((row) => (
                  <tr key={row.month}>
                    <td>{monthLabel(row.month)}{row.complete ? '' : <span className={s.muted}> (to date)</span>}</td>
                    <td style={{ textAlign: 'right' }}>{row.startCount}</td>
                    <td style={{ textAlign: 'right' }}>{row.newCount}</td>
                    <td style={{ textAlign: 'right' }}>{row.churnedCount}</td>
                    <td style={{ textAlign: 'right' }}>{percent(row.churnRate)}</td>
                    <td style={{ textAlign: 'right' }}>{usd(row.startMrrCents)}</td>
                    <td style={{ textAlign: 'right' }}>{usd(row.churnedMrrCents)}</td>
                    <td style={{ textAlign: 'right' }}>{percent(row.grossRevenueChurn)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {r && <p className={s.muted} style={{ fontSize: '0.8rem' }}>{r.canceled90d} subscription{r.canceled90d === 1 ? '' : 's'} ended in the last 90 days · {r.customers} Stripe customers (team left out){r.unlinkedSubscriptions ? ` · ${r.unlinkedSubscriptions} Stripe subscription${r.unlinkedSubscriptions === 1 ? '' : 's'} not tied to an account` : ''}.</p>}
      </Section>

      <Section title="By price" summary="what each price brings in now" count={r ? r.byPrice.length : null} id="prices">
        {!r ? <div className={s.empty}>{NOT_MEASURED}</div> : r.byPrice.length === 0 ? <div className={s.empty}>No paying subscriptions.</div> : (
          <div className={s.tableWrap}>
            <table className={s.table} data-table="prices">
              <thead><tr><th>Price</th><th>Billed</th><Th right>List</Th><Th right>Subscriptions</Th><Th k="mrr" right>MRR after coupons</Th></tr></thead>
              <tbody>
                {r.byPrice.map((p) => (
                  <tr key={p.priceId}><td><code>{p.priceId}</code></td><td>{p.interval ? INTERVAL_LABEL[p.interval] ?? p.interval : NOT_MEASURED}</td><td style={{ textAlign: 'right' }}>{usd(p.listCents)}</td><td style={{ textAlign: 'right' }}>{p.subscriptions}</td><td style={{ textAlign: 'right' }}>{usd(p.mrrCents)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section title="Founding members" summary={split ? `${split.coupon} on the founding price${split.intro ? ` · ${split.intro} with the earlier intro` : ''} · codes closed ${ymd(b.founding.codesEndOn)}` : NOT_MEASURED} count={split ? split.coupon : null} defaultOpen id="founding">
        {!fs ? <div className={s.empty}>{NOT_MEASURED}: Stripe did not answer.</div> : fs.length === 0 ? <div className={s.empty}>Nobody is paying the founding price.</div> : (
          <div className={s.tableWrap}>
            <table className={s.table} data-table="founding">
              <thead><tr><th>Member</th><th>Plan</th><th style={{ textAlign: 'right' }}>Pays</th><th>Since</th><th>Renews</th><th title={DEFINITIONS.founding}>Founding price ends</th></tr></thead>
              <tbody>
                {fs.map((f, i) => (
                  <tr key={f.userId ?? i} data-founding-row={f.userId ?? ''}>
                    <td>{personLink(f.userId, f.name, f.email)}</td>
                    <td>{f.interval ? INTERVAL_LABEL[f.interval] ?? f.interval : NOT_MEASURED}</td>
                    <td style={{ textAlign: 'right' }}>{usd(f.payingCents)} <span className={s.muted}>list {usd(f.listCents)}</span></td>
                    <td>{ymd(f.since)}</td>
                    <td>{ymd(f.renewsAt)}</td>
                    <td>{foundingEnds(f)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className={s.muted} style={{ fontSize: '0.8rem' }}>
          Founding member program (comps): {b.founding.program.length === 0 ? 'nobody added yet' : b.founding.program.map((p) => `${p.name || p.email || p.userId.slice(0, 8)} (${p.status}, comp ${p.compEndsAt ? `to ${ymd(p.compEndsAt)}` : 'none'})`).join(' · ')}. <Link to="/founding">Founding page</Link>
        </p>
      </Section>

      <Section title="Sign-ups to retention, by sign-up week" summary={`${b.cohorts.totals.signups} sign-ups in ${b.cohorts.weeks.length} weeks · first check ${rateLabel(b.cohorts.totals.firstCheck)} · day 7 ${rateLabel(b.cohorts.totals.d7)}`} defaultOpen id="cohorts">
        <div className={s.tableWrap}>
          <table className={s.table} data-table="cohorts">
            <thead><tr><th>Week of</th><Th k="signups" right>Sign-ups</Th><Th k="firstCheck">First check</Th><Th k="dayTwo">Day two</Th><Th k="d7">Day 7</Th><Th k="d30">Day 30</Th></tr></thead>
            <tbody>
              {b.cohorts.weeks.map((w) => (
                <tr key={w.weekStart} data-week={w.weekStart}>
                  <td>{w.weekStart}</td>
                  <td style={{ textAlign: 'right' }}>{w.signups}</td>
                  <td>{w.signups ? rateLabel(w.firstCheck) : '·'}</td>
                  <td>{w.signups ? rateLabel(w.dayTwo) : '·'}</td>
                  <td>{w.signups ? rateLabel(w.d7) : '·'}</td>
                  <td>{w.signups ? rateLabel(w.d30) : '·'}</td>
                </tr>
              ))}
              <tr>
                <td><strong>All {b.cohorts.weeks.length} weeks</strong></td>
                <td style={{ textAlign: 'right' }}><strong>{b.cohorts.totals.signups}</strong></td>
                <td>{rateLabel(b.cohorts.totals.firstCheck)}</td>
                <td>{rateLabel(b.cohorts.totals.dayTwo)}</td>
                <td>{rateLabel(b.cohorts.totals.d7)}</td>
                <td>{rateLabel(b.cohorts.totals.d30)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Section>

      <Section title="Accounts" summary={`${num(b.accounts.members)} members · ${b.accounts.teamExcluded} team and QA accounts left out`} id="accounts">
        <div className={s.strip}>
          <Metric k="members" label="Members" value={num(b.accounts.members)} sub={`${b.accounts.teamExcluded} team and QA left out`} />
          {(['email', 'google', 'apple', 'facebook', 'unknown'] as const).map((k) => (
            <Metric key={k} k="members" label={k === 'unknown' ? 'No sign-in on record' : `Signed up with ${k === 'email' ? 'email' : k[0].toUpperCase() + k.slice(1)}`} value={num(b.accounts.byMethod[k])} />
          ))}
        </div>
      </Section>
    </div>
  );
}

export default function BusinessPage() {
  const q = useBusiness();
  const qc = useQueryClient();
  const [refreshing, setRefreshing] = useState(false);
  const refresh = async () => {
    setRefreshing(true);
    try {
      qc.setQueryData(['business'], await fetchBusiness(true));
    } finally {
      setRefreshing(false);
    }
  };
  if (q.isLoading) return <div className={s.empty}>Reading Stripe and the database…</div>;
  if (q.isError || !q.data) return <div className={s.notice}>The business numbers did not load. {q.error instanceof Error ? q.error.message : ''}</div>;
  return <BusinessView b={q.data} onRefresh={() => void refresh()} refreshing={refreshing} />;
}
